import {
  ACCOUNT_SHARING_LOGIN_NOTICE_BANNER_SELECTOR,
  USETIFUL_OPEN_URL_ACTION_SELECTOR,
  containsAccountSharingCtaMarker
} from '~/utils/accountSharingLoginNotice'

export const ACCOUNT_SHARING_BANNER_EVENTS = Object.freeze({
  show: 'Show - Banner',
  talkToUs: 'Click - Banner - Talk to us',
  close: 'Click - Banner - Close'
})

const OBSERVER_OPTIONS = {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['class', 'data-action', 'data-start-url', 'href']
}

function isRiskLevel(value) {
  return value === 'high' || value === 'medium'
}

export function elementContainsAccountSharingMarker(element) {
  if (!element || typeof element.getAttribute !== 'function') return false

  return ['data-start-url', 'href'].some(attribute =>
    containsAccountSharingCtaMarker(element.getAttribute(attribute))
  )
}

export function bannerHasAccountSharingMarker(banner) {
  return Array.from(
    banner.querySelectorAll(USETIFUL_OPEN_URL_ACTION_SELECTOR)
  ).some(elementContainsAccountSharingMarker)
}

function findVisibleAccountSharingBanner() {
  return Array.from(
    document.querySelectorAll(ACCOUNT_SHARING_LOGIN_NOTICE_BANNER_SELECTOR)
  ).find(
    banner =>
      banner.classList.contains('uf-visible') &&
      bannerHasAccountSharingMarker(banner)
  )
}

function eventProperties(riskLevel) {
  return {
    banner_type: 'account sharing notice',
    product: 'lighthouse',
    risk_level: riskLevel,
    $set: { risk_level: riskLevel }
  }
}

export function createAccountSharingLoginNoticeTracker({ capture }) {
  let destroyed = false
  let started = false
  let observer = null
  let scheduledFrame = null
  let wasVisible = false
  let visibleBanner = null
  let trackingStatus = 'ineligible'
  let riskLevel = null
  const deferredEvents = []

  function captureEvent(eventName) {
    if (destroyed) return

    if (trackingStatus === 'eligible' && isRiskLevel(riskLevel)) {
      capture(eventName, eventProperties(riskLevel))
    } else if (trackingStatus === 'pending') {
      deferredEvents.push(eventName)
    }
  }

  function evaluateVisibility() {
    scheduledFrame = null
    if (destroyed) return

    const markedBanner = findVisibleAccountSharingBanner()
    const retainedBannerIsVisible =
      visibleBanner &&
      document.body.contains(visibleBanner) &&
      visibleBanner.classList.contains('uf-visible')
    const isVisible = Boolean(markedBanner || retainedBannerIsVisible)
    visibleBanner =
      markedBanner || (retainedBannerIsVisible ? visibleBanner : null)
    if (isVisible && !wasVisible) {
      captureEvent(ACCOUNT_SHARING_BANNER_EVENTS.show)
    }
    wasVisible = isVisible
  }

  function scheduleEvaluation() {
    if (destroyed || scheduledFrame !== null) return
    scheduledFrame = window.requestAnimationFrame(evaluateVisibility)
  }

  return {
    start() {
      if (destroyed || started) return
      started = true
      observer = new window.MutationObserver(scheduleEvaluation)
      observer.observe(document.body, OBSERVER_OPTIONS)
      scheduleEvaluation()
    },

    updateTrackingContext(nextStatus, nextRiskLevel) {
      if (destroyed) return

      if (nextStatus === 'pending') {
        trackingStatus = 'pending'
        riskLevel = null
        return
      }

      if (nextStatus === 'eligible' && isRiskLevel(nextRiskLevel)) {
        trackingStatus = 'eligible'
        riskLevel = nextRiskLevel
        deferredEvents.splice(0).forEach(captureEvent)
        return
      }

      trackingStatus = 'ineligible'
      riskLevel = null
      deferredEvents.length = 0
    },

    trackTalkToUs() {
      captureEvent(ACCOUNT_SHARING_BANNER_EVENTS.talkToUs)
    },

    trackClose() {
      captureEvent(ACCOUNT_SHARING_BANNER_EVENTS.close)
    },

    destroy() {
      if (destroyed) return
      destroyed = true
      if (observer) observer.disconnect()
      if (scheduledFrame !== null) {
        window.cancelAnimationFrame(scheduledFrame)
      }
      scheduledFrame = null
      deferredEvents.length = 0
    }
  }
}
